/**
 * OnboardingContext
 *
 * Manages the onboarding wizard state for Access Time.
 * Two distinct "done" states:
 *   - dismissed: React state only, resets on page refresh. Suppresses auto-show.
 *   - completed: Persisted to backend via PUT /api/settings/timesheet (OnboardingCompletedAt).
 *     localStorage is used as a fast fallback; the backend value is authoritative.
 *
 * localStorage keys (per organisationId):
 *   timetrackpro_onboarding_completed_<organisationId>
 *   timetrackpro_onboarding_step_<organisationId>
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useSettings } from '@/contexts/SettingsContext';
import { apiClient } from '@/services/apiClient';
import { prefixKey } from '@/utils/storagePrefix';
import type { OrganisationTimesheetSettingsDto } from '@/types/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OnboardingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8; // 0=Welcome, 1-7=steps, 8=Completion

interface OnboardingContextType {
  // State
  hasDismissed: boolean;
  hasCompleted: boolean;
  isWizardOpen: boolean;
  savedStep: OnboardingStep;

  // Actions
  openWizard: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  saveStep: (step: OnboardingStep) => void;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function completedKey(orgId: string) {
  return prefixKey(`timetrackpro_onboarding_completed_${orgId}`);
}

function stepKey(orgId: string) {
  return prefixKey(`timetrackpro_onboarding_step_${orgId}`);
}

function readCompleted(orgId: string | undefined): boolean {
  if (!orgId) return false;
  try {
    return localStorage.getItem(completedKey(orgId)) === 'true';
  } catch {
    return false;
  }
}

function readSavedStep(orgId: string | undefined): OnboardingStep {
  if (!orgId) return 0;
  try {
    const raw = localStorage.getItem(stepKey(orgId));
    if (raw !== null) {
      const parsed = parseInt(raw, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 8) {
        return parsed as OnboardingStep;
      }
    }
  } catch {
    // ignore
  }
  return 0;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { evoOrganisationId } = useAuth();
  const { organizationSettings } = useSettings();

  const [hasDismissed, setHasDismissed] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(() => readCompleted(evoOrganisationId));
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [savedStep, setSavedStep] = useState<OnboardingStep>(() => readSavedStep(evoOrganisationId));

  // Re-derive state when the organisationId becomes available (e.g. after BootstrapGate)
  useEffect(() => {
    if (evoOrganisationId) {
      setHasCompleted(readCompleted(evoOrganisationId));
      setSavedStep(readSavedStep(evoOrganisationId));
    }
  }, [evoOrganisationId]);

  // Sync hasCompleted from backend settings (authoritative source)
  useEffect(() => {
    if (organizationSettings?.onboardingCompletedAt) {
      setHasCompleted(true);
      // Also sync localStorage so future loads are fast
      if (evoOrganisationId) {
        try {
          localStorage.setItem(completedKey(evoOrganisationId), 'true');
        } catch {
          // ignore
        }
      }
    }
  }, [organizationSettings?.onboardingCompletedAt, evoOrganisationId]);

  const openWizard = useCallback(() => {
    setIsWizardOpen(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    // React state only — does NOT write to localStorage
    setHasDismissed(true);
    setIsWizardOpen(false);
  }, []);

  const completeOnboarding = useCallback(() => {
    setHasCompleted(true);
    setIsWizardOpen(false);
    if (evoOrganisationId) {
      try {
        localStorage.setItem(completedKey(evoOrganisationId), 'true');
      } catch {
        // ignore storage errors
      }
    }

    // Persist to backend: fetch current settings then PUT with OnboardingCompletedAt set
    apiClient.get<OrganisationTimesheetSettingsDto>('/settings/timesheet')
      .then((currentSettings) => {
        const updatedSettings = {
          ...currentSettings,
          onboardingCompletedAt: new Date().toISOString(),
        };
        return apiClient.put<OrganisationTimesheetSettingsDto>('/settings/timesheet', updatedSettings);
      })
      .catch(() => {
        // Best-effort: localStorage is already set as fallback
      });
  }, [evoOrganisationId]);

  const resetOnboarding = useCallback(() => {
    // Clear localStorage and reset all state — no page refresh needed
    if (evoOrganisationId) {
      try {
        localStorage.removeItem(completedKey(evoOrganisationId));
        localStorage.removeItem(stepKey(evoOrganisationId));
      } catch {
        // ignore
      }
    }
    setHasDismissed(false);
    setHasCompleted(false);
    setIsWizardOpen(true);
    setSavedStep(0);
  }, [evoOrganisationId]);

  const saveStep = useCallback(
    (step: OnboardingStep) => {
      setSavedStep(step);
      if (evoOrganisationId) {
        try {
          localStorage.setItem(stepKey(evoOrganisationId), String(step));
        } catch {
          // ignore
        }
      }
    },
    [evoOrganisationId],
  );

  const value: OnboardingContextType = {
    hasDismissed,
    hasCompleted,
    isWizardOpen,
    savedStep,
    openWizard,
    dismissOnboarding,
    completeOnboarding,
    resetOnboarding,
    saveStep,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextType {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}
